// Meta de Frentista com IA (feature 161): espelho do contrato do backend
// (docs/superpowers/plans/2026-10-07-meta-frentista-ia-contrato.md, seção 5).
// Sem nome de frentista e sem tag em nenhum campo: o convite só carrega agregados.
import type { AttendantGoalType } from './attendantGoalType';

export enum OfferStatus {
  pending = 'pending',
  active = 'active',
  ended = 'ended',
  /** "Agora não" e convite fechado sozinho só aparecem nas telas internas da PontuaX; a rota da rede nunca os devolve. */
  dismissed = 'dismissed',
  superseded = 'superseded',
}

export enum OfferEndedReason {
  expired = 'expired',
  converted = 'converted',
}

export enum GoalLevel {
  conservative = 'conservative',
  balanced = 'balanced',
  challenging = 'challenging',
}

export enum GoalTrigger {
  lowTail = 'low_tail',
  loyaltyGap = 'loyalty_gap',
}

/** A meta principal do posto é quantidade ou fidelização; as demais metas da degustação também podem ser volume. */
export type OfferTargetType = AttendantGoalType.QUANTITY | AttendantGoalType.PERCENTAGE_LOYALTY | AttendantGoalType.VOLUME;

/** Os três momentos da IA que o convite expõe (`kind` da rota ai-insight). */
export enum InsightKind {
  offerPitch = 'offer_pitch',
  trialWeekly = 'trial_weekly',
  trialFinal = 'trial_final',
}

export enum InsightStatus {
  ready = 'ready',
  generating = 'generating',
  unavailable = 'unavailable',
  insufficientData = 'insufficient_data',
}

export enum InsightVerdict {
  positive = 'positive',
  neutral = 'neutral',
  negative = 'negative',
  noData = 'no_data',
}

/** 409 do backend (`Boom.conflict(code)`): o código vem em `body.message`. Rede alheia é 404. */
export enum OfferErrorCode {
  offerNotPending = 'offer_not_pending',
  staleSuggestion = 'stale_suggestion',
  moduleAlreadyActive = 'module_already_active',
  notChainEmployee = 'not_chain_employee',
  continueNotAvailable = 'continue_not_available',
  moduleInactive = 'module_inactive',
  noStationAvailable = 'no_station_available',
  insightNotAvailable = 'insight_not_available',
  boardRefused = 'board_refused',
  invalidLevels = 'invalid_levels',
}

export interface InsightItem { kind: string; text: string }
export interface InsightContent { headline: string; items: InsightItem[] }
export interface InsightDecisions {
  recommendation?: string;
  /** Por posto, uma recomendação por quadro (a degustação tem um quadro por meta). */
  recommendations?: Record<string, string[]>;
  /** Só na leitura final: por posto, um veredito por meta. */
  stationVerdicts?: Record<string, string[]>;
  verdict?: InsightVerdict;
}
export interface InsightResponse {
  status: InsightStatus;
  retryAfterSeconds?: number;
  kind: string;
  content?: InsightContent;
  decisions?: InsightDecisions;
}

export interface OfferLevelOption {
  level: GoalLevel;
  value: number;
  daysHitRatePct: number;
  attendantsReaching: number;
}

/** Meta adicional do posto (a ativação cria um quadro dela no nível recomendado): `value` é o alvo que será criado. */
export interface OfferExtraGoal {
  targetType: OfferTargetType;
  recommended: GoalLevel;
  value: number;
  options: OfferLevelOption[];
}

/** Quadro criado pela ativação (um por meta em cada posto). `targetType` = alvo do quadro; `null` só se o quadro ficou sem alvo. */
export interface OfferStationBoard {
  id: string;
  name: string;
  targetType: AttendantGoalType | null;
}

export interface OfferStationView {
  placeId: string;
  placeName: string;
  targetType: OfferTargetType;
  triggers: GoalTrigger[];
  eligibleAttendants: number;
  tailSize: number;
  tailIndexPct: number | null;
  stationAppPct: number | null;
  loyaltyLowCount: number;
  options: OfferLevelOption[];
  singleLevel: boolean;
  recommended: GoalLevel;
  levelChosen: GoalLevel | null;
  /** Quadro da meta principal (o primeiro criado no posto). Mantido por compatibilidade; a lista completa está em `boards`. */
  boardId: string | null;
  boardName: string | null;
  /**
   * Demais metas do posto (quantidade, fidelização, volume, menos a principal). Convite antigo: lista vazia.
   * Backend anterior a esta versão nem manda o campo: quem lê trata `undefined` como lista vazia.
   */
  extraGoals: OfferExtraGoal[];
  /**
   * Todos os quadros do posto depois da ativação, o da meta principal primeiro. Convite pendente: lista vazia.
   * Backend anterior a esta versão nem manda o campo: quem lê trata `undefined` como lista vazia (e usa `boardId`).
   */
  boards: OfferStationBoard[];
}

export interface OfferView {
  id: string;
  status: OfferStatus;
  endedReason: OfferEndedReason | null;
  suggestionVersion: number;
  baselineStart: string;
  baselineEnd: string;
  activatedAt: string | null;
  trialEndsAt: string | null;
  dayNumber: number | null;
  daysLeft: number | null;
  canContinue: boolean;
  continueRequestedAt: string | null;
  totals: {
    stationsAnalyzed: number;
    stationsWithTrigger: number;
    tailAttendants: number;
    loyaltyLowAttendants: number;
  };
  stations: OfferStationView[];
}

export class OfferApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: OfferErrorCode | null,
    /** Só no 409 stale_suggestion: o convite com a sugestão nova. */
    readonly offer: OfferView | null,
  ) {
    super(message);
    this.name = 'OfferApiError';
  }
}

/**
 * Helper único de leitura das respostas do convite (emenda 10): aceita `{ data: [x] }`, `{ data: x }`,
 * `{ data: [null] }`, `{ data: [] }`, `{ data: null }` e `undefined`; tudo que não é objeto vira `null`.
 */
export const unwrapData = <T>(json: any): T | null => {
  const data = json?.data;
  return ((Array.isArray(data) ? data[0] : data) ?? null) as T | null;
};

const ERROR_CODES: string[] = Object.values(OfferErrorCode);

/** Erro do `fetchJson` do react-admin (`.status`, `.body`) vira `OfferApiError`; código desconhecido fica `null`. */
export const toOfferApiError = (err: any): OfferApiError => {
  const body = err?.body ?? {};
  const message = String(body.message ?? err?.message ?? '');
  const code = ERROR_CODES.includes(message) ? (message as OfferErrorCode) : null;
  const offer = (body.data?.offer ?? body.offer ?? null) as OfferView | null;
  return new OfferApiError(message, Number(err?.status ?? 0), code, offer);
};

// ---- Telas internas da PontuaX: GET /attendant-goal-offers e /attendant-goal-worker-runs (isAdmin + attendant-goal-internal) ----

/** Lançamento do convite: rede de prévia, piloto escolhido a dedo, geral ou enviado à mão pelo botão "Enviar convite". */
export enum OfferRollout {
  preview = 'preview',
  pilot = 'pilot',
  standard = 'standard',
  manual = 'manual',
}

export enum WorkerRunStatus {
  running = 'running',
  done = 'done',
}

/** Por que um posto ficou fora da análise da semana (chaves de `summary.excluded` do worker). */
export enum StationExclusion {
  noData = 'no_data',
  smallTeam = 'small_team',
  sharedCard = 'shared_card',
  lowCoverage = 'low_coverage',
  numericIdsMismatch = 'numeric_ids_mismatch',
}

/** Uma linha de `triggers.stations` do convite (todo posto analisado, com ou sem gatilho). */
export interface InternalTriggerStation {
  placeId: string;
  triggers: GoalTrigger[];
  eligibleAttendants: number;
  tailSize: number;
  tailIndexPct: number | null;
  stationAppPct: number | null;
  loyaltyLowCount: number;
}

/** Uma linha de `suggestion.stations` do convite (só os postos com gatilho). */
export interface InternalSuggestionStation {
  placeId: string;
  targetType: OfferTargetType;
  options: OfferLevelOption[];
  recommended: GoalLevel;
}

export interface InternalInsight {
  id: string;
  kind: InsightKind;
  locale: string;
  weekStart: string | null;
  status: InsightStatus;
  attempts: number;
  costUsd: number | null;
  generatedAt: string | null;
}

/** Linha da lista de convites; `rollout` pode faltar em backend antigo. */
export interface InternalOfferRow {
  id: string;
  chainId: string;
  status: OfferStatus;
  endedReason: OfferEndedReason | null;
  rollout?: OfferRollout | null;
  /** Quem da PontuaX enviou o convite à mão (`rollout` manual); pode faltar em backend antigo. */
  sentByEmployeeId?: string | null;
  suggestionVersion: number;
  baselineStart: string;
  baselineEnd: string;
  createdAt: string;
  firstSeenAt: string | null;
  dismissedAt: string | null;
  dismissedByEmployeeId: string | null;
  activatedAt: string | null;
  activatedByEmployeeId: string | null;
  trialEndsAt: string | null;
  endedAt: string | null;
  continueRequestedAt: string | null;
  continueRequestedByEmployeeId: string | null;
  stationsAnalyzed: number;
  stationsWithTrigger: number;
  targetTypes: string[];
}

/** Convite completo (GET /attendant-goal-offers/{id}). */
export interface InternalOffer extends InternalOfferRow {
  triggers: { stations?: InternalTriggerStation[] } | null;
  suggestion: { stations?: InternalSuggestionStation[] } | null;
  levelsChosen: Record<string, GoalLevel> | null;
  boardIds: string[] | null;
  lastWeeklyNotifiedWeek: string | null;
  endingNotifiedAt: string | null;
  insights: InternalInsight[] | null;
}

/** Linha da lista de execuções semanais (`id` = segunda-feira da semana, 'YYYY-MM-DD'). */
export interface InternalWorkerRun {
  id: string;
  weekStart: string;
  status: WorkerRunStatus;
  dryRun: boolean;
  startedAt: string;
  durationSeconds: number | null;
  chainsEvaluated: number;
  stationsAnalyzed: number;
  t1: number;
  t2: number;
  chainsWithTrigger: number;
  offersCreated: number;
  offersRefreshed: number;
  offersClosedNoTrigger: number;
  errors: number;
  excluded: Partial<Record<StationExclusion, number>>;
}

// ---- Envio manual do convite: POST /attendant-goal-offers/send (isAdmin + attendant-goal-internal) ----

/** `created` = convite novo; `refreshed` = a rede já tinha um e ele foi recalculado. */
export enum SendOfferOutcome {
  created = 'created',
  refreshed = 'refreshed',
}

export interface SendOfferResult {
  outcome: SendOfferOutcome;
  offerId: string;
  chainId: string;
  stationsAnalyzed: number;
  stationsWithTrigger: number;
  targetTypes: string[];
}

/** Recusas do envio (`body.message` do Boom): 409, exceto `chain_not_found` (404) e `analysis_unavailable` (503). */
export enum SendOfferErrorCode {
  chainNotFound = 'chain_not_found',
  moduleActive = 'module_active',
  hasBoards = 'has_boards',
  trialAlreadyUsed = 'trial_already_used',
  dismissedRecently = 'dismissed_recently',
  /** A consulta de elegibilidade recusou a rede por um motivo que o diagnóstico não achou (corrida entre as duas leituras). */
  notEligible = 'not_eligible',
  noStation = 'no_station',
  noStationAnalyzable = 'no_station_analyzable',
  noTrigger = 'no_trigger',
  sendInProgress = 'send_in_progress',
  analysisUnavailable = 'analysis_unavailable',
}

/** `body.data` de algumas recusas: quando o dono clicou em "Agora não", postos com ids divergentes e postos excluídos por motivo. */
export interface SendOfferErrorData {
  dismissedAt?: string;
  /** Contagem, ou a lista de postos divergentes. */
  mismatched?: number | unknown[];
  excluded?: Partial<Record<StationExclusion, number>>;
}

export class SendOfferError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: SendOfferErrorCode | null,
    readonly data: SendOfferErrorData | null,
  ) {
    super(message);
    this.name = 'SendOfferError';
  }
}

const SEND_ERROR_CODES: string[] = Object.values(SendOfferErrorCode);

/** Erro do `fetchJson` do react-admin (`.status`, `.body`) vira `SendOfferError`; código desconhecido fica `null`. */
export const toSendOfferError = (err: any): SendOfferError => {
  const body = err?.body ?? {};
  const message = String(body.message ?? err?.message ?? '');
  const code = SEND_ERROR_CODES.includes(message) ? (message as SendOfferErrorCode) : null;
  const data = body.data && typeof body.data === 'object' ? (body.data as SendOfferErrorData) : null;
  return new SendOfferError(message, Number(err?.status ?? 0), code, data);
};
